import { action, reaction, makeObservable } from 'mobx';
import { v4 as uuid } from 'uuid';
import { Store } from './store';
import { createMessage, MessageHandler } from './message';
import { BindingPropValue, PropChangeListener, PropManager } from './propManager';
import { StoreNodeProxy } from './storeNodeProxy';
import { DebugContext } from '../core/debug';
import pick from 'lodash/pick';

export interface IStoreNode {
  listen(listener: MessageHandler): void;
  unlisten(listener: MessageHandler): void;

  invoke(type: string, payload?: unknown): void | Promise<void>;
}

type PropNames<TNode extends StoreNode> = keyof TNode['propManager']['props'] & string;
type PropValue<
  T extends PropNames<TNode>,
  TNode extends StoreNode> = TNode['propManager']['props'][T];

export interface StoreNode {
  /** Set to true if the Entity itself has been generated as a Mock. */
  _isMock?: boolean;
  _mocker?: unknown;
  _debugger?: DebugContext
}

/**
 * Props for StoreNode constructors are intentionally untyped to support
 * arbitrary prop shapes across the store node hierarchy.
 */
// TODO: Replace `any` with a stricter type (e.g. Record<string, unknown>).
// Blocked because prop objects throughout the store node hierarchy contain
// strongly-typed values (functions, nested objects) that are incompatible
// with `unknown` without widespread refactoring of PropManager generics.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- single canonical location for the StoreNode props type
export type StoreNodeProps = any;

/** Base class for all objects that are part of the store tree. */

export class StoreNode {

  constructor(store: Store, props?: StoreNodeProps) {
    makeObservable(this, {
      setProp: action,
    });

    this.store = store || null;
    this.proxy = new StoreNodeProxy<StoreNode>(this, store);

    this.props = props;
    this.propManager = new PropManager(props, this);

    if (import.meta.env.DEV) {
      this._isMock = false;
      this._mocker = null;
      this._debugger = {
        color: '#999',
        label: () => this.nodeType,
        id: () => this.id
      }
    }
  }

  nodeTags: string[] = [];

  protected readonly proxy: StoreNodeProxy<StoreNode>;

  readonly store: Store;
  readonly id: string = uuid();
  readonly nodeType: string = 'Node';

  readonly props: this['propManager']['props'];
  readonly propManager: PropManager;

  protected get resolvedProps() {
    return this.propManager.resolvedProps;
  }

  setProp<T extends keyof this['propManager']['props'] & string>(
    name: T,
    value: BindingPropValue<this['propManager']['props']>) {
    this.propManager.set(name, value);
    return this;
  }

  protected getProp<T extends PropNames<this>>(
    name: T,
    fallback?: PropValue<T, this>) {
    return this.propManager.get(name, fallback);
  }

  protected getResolvedProp<T extends PropNames<this>>(
    name: T,
    fallback?: BindingPropValue<this['propManager']['props'][T], [this]>) {
    return this.propManager.getResolved(name, fallback);
  }

  // #region emit
  // -------
  async emit(type: string, payload?: unknown): Promise<void> {
    await this.proxy.emit(type, payload);
  }
  emitHandler(type: string, payload?: unknown) {
    return (_evt: unknown) => this.emit(type, payload);
  }
  // #endregion

  // #region broadcast
  // -------
  broadcast(type: string, payload?: unknown) {
    this.proxy.broadcast(type, payload);
    return this;
  }
  broadcastHandler(type: string, payload?: unknown) {
    return (_evt: unknown) => this.broadcast(type, payload);
  }
  // #endregion

  // #region dispatch
  // -------
  dispatch(type: string, payload?: unknown): this;
  dispatch(target: string, type: string, payload?: unknown): this;

  dispatch(...args: [string, unknown?] | [string, string, unknown?]) {

    if (
      typeof args[0] === 'string' &&
      typeof args[1] === 'string') {

      this.proxy.dispatch(args[0], args[1], args[2]);
    } else {
      this.proxy.dispatch(null, args[0], args[1]);
    }

    return this;
  }

  dispatchHandler(type: string, payload?: unknown) {
    return (_evt: unknown) => this.dispatch(type, payload);
  }
  // #endregion

  listen(listener: MessageHandler<this>) {
    this.proxy.listen(listener as MessageHandler<StoreNode>);
  }

  unlisten(listener: MessageHandler<this>) {
    this.proxy.unlisten(listener as MessageHandler<StoreNode>);
  }

  invoke(_type: string, _payload?: unknown, _source?: unknown): void { /* noop */ }
  invokeHandler(type: string, payload?: unknown) {
    return (evt: unknown) => this.invoke(type, payload, evt);
  }

  protected receive(handler: MessageHandler, filter: string[] = []) {
    this.proxy.receive(handler, filter);
  }

  isDisposed: boolean = false;
  dispose() {
    this.isDisposed = true;
    this.propManager.disposeReactions();
  }

  protected onPropChange(propName: string, listener: PropChangeListener) {
    this.propManager.onChange(propName, listener);
  }

  protected onPropDiscard(propName: string, listener: PropChangeListener) {
    this.propManager.onDiscard(propName, listener);
  }

  protected autoListen<T extends PropNames<this>>(
    propName: string,
    listener: MessageHandler<PropValue<T, this>>,
    emitUpdates = true) {

    this.onPropChange(propName, (node: PropValue<T, this>, prevNode: PropValue<T, this>) => {

      if (!((node as unknown) instanceof StoreNode)) {
        if (node)
          console.warn(`'onPropChange' handler invoked by 'autoListener' failed because the resulting node is not a StoreNode.`);
        return;
      }
      node.listen(listener);

      if (emitUpdates && typeof listener === 'function') {
        const msg = createMessage(this, 'emit', 'propChange', {
          previousValue: prevNode,
          value: node
        });

        listener(msg);
      }
    });

    this.onPropDiscard(propName, (node: PropValue<T, this>, nextNode: PropValue<T, this>) => {

      if (!((node as unknown) instanceof StoreNode)) {
        if (node)
          console.warn(`'onPropDiscard' handler invoked by 'autoListener' failed because the resulting node is not a StoreNode.`);
        return;
      }
      node.unlisten(listener);

      if (emitUpdates && typeof listener === 'function') {
        const msg = createMessage(this, 'emit', 'propDiscard', {
          nextValue: nextNode,
          value: node
        });

        listener(msg);
      }
    });
  }

  protected onFieldChange(propName: string, listener: PropChangeListener) {

    const reactionOpts = {
      fireImmediately: true
    };

    reaction(() => (this as Record<string, unknown>)[propName],
      (value, prevValue) =>
        listener(value, prevValue),
      reactionOpts);

    return this;
  }

  // #region Debug members
  _getState?: (() => Record<string, unknown>);
  _logState?: (() => void);

  // #endregion
}

if (import.meta.env.DEV) {
  StoreNode.prototype._getState = function() {
    return pick(this, Object.keys(this));
  }

  StoreNode.prototype._logState = function() {
    console.log(this._getState?.());
  }
}